import { useAtomValue, useSetAtom } from "jotai";
import { AlertTriangle, ArrowLeft, ChevronRight, MapPin, PiggyBank, Pin, Search } from "lucide-react";
import { useLocation } from "wouter";
import LogoSymbol from "@/assets/logo-symbol-transparent-79x96.png";
import LogoWordmark from "@/assets/logo-wordmark-256x48.png";
import { Button } from "@/components/ui/Button";
import { assignAccountAtom, availableAccountsAtom, logoutAtom } from "@/store";
import { useMemo, useState } from "react";
import { useAccountProperties } from "@/hooks/useAccountProperties";

function AccountSelectionPage() {
  const accounts = useAtomValue(availableAccountsAtom);
  const { getProperties } = useAccountProperties();
  const [, setLocation] = useLocation();
  const assignAccount = useSetAtom(assignAccountAtom);
  const logout = useSetAtom(logoutAtom);
  const isLoading = accounts === undefined || accounts.length === 0;
  const [filter, setFilter] = useState("");

  const handleAccountSelect = async (accountId: string) => {
    try {
      await assignAccount({ accountUserId: accountId, navigate: setLocation });
    } catch (error) {
      console.error("Failed to assign account:", error);
      // Error handling is already done in the atom
    }
  };

  const handleBackToLogin = () => {
    logout().then(() => setLocation("/login"));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const filteredAndSortedAccounts = useMemo(() => {
    if (!accounts) return [];

    const filtered = accounts.filter((accountUser) => {
      const owner = accountUser.account?.owner;
      const properties = getProperties(accountUser.id);
      const searchTerm = filter.toLowerCase();
      const email = owner?.email?.toLowerCase() || "";
      const firstName = owner?.first_name?.toLowerCase() || "";
      const lastName = owner?.last_name?.toLowerCase() || "";
      const propertyNames = properties.map((p) => p.name.toLowerCase()).join(" ");

      return (
        email.includes(searchTerm) || firstName.includes(searchTerm) ||
        lastName.includes(searchTerm) || propertyNames.includes(searchTerm)
      );
    });

    return filtered.sort((a, b) => {
      const emailA = a.account?.owner?.first_name || "";
      const emailB = b.account?.owner?.first_name || "";
      return emailA.localeCompare(emailB);
    });
  }, [accounts, filter, getProperties]);

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando suas contas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 py-4 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {false && (
            <div className="mx-auto w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          )}
          <div className="mx-auto flex flex-col items-center text-center">
            <img src={LogoSymbol} alt="Description" className="mb-4 object-cover h-16" />
            <img src={LogoWordmark} alt="Irriga+ Wordmark" className="h-8 mb-2" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Selecione uma Conta</h1>
          <p className="text-gray-600">Você tem acesso a múltiplas contas. Escolha qual deseja acessar.</p>
        </div>

        {/* Filter Input */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Filtrar por email, nome ou sobrenome..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Accounts Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mt-4">
          {filteredAndSortedAccounts.map((accountUser) => {
            const acc = accountUser.account;
            const owner = acc?.owner;
            const role = accountUser.role;
            const properties = getProperties(accountUser.id);
            return (
              <div
                key={accountUser.id}
                className="bg-white rounded-2xl shadow-lg border border-green-200/60 overflow-hidden hover:shadow-xl transition-shadow duration-200 cursor-pointer group flex flex-col "
                onClick={() => handleAccountSelect(accountUser.id)}
              >
                <div className="flex-1 p-4 flex flex-col justify-between">
                  {/* Header with Owner Info */}
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex-1">
                        {/* Owner */}
                        <div className="text-base font-bold text-gray-900 truncate ">
                          {owner?.first_name || owner?.last_name ? (
                            <>
                              {owner?.first_name} {owner?.last_name}
                            </>
                          ) : (
                            <span className="italic text-gray-400">Sem nome cadastrado</span>
                          )}
                        </div>
                        {/* Email */}
                        <div className="text-sm text-gray-600 truncate mb-2" title={owner?.email || ""}>
                          {owner?.email || "(sem email)"}
                        </div>
                        {/* Properties Section */}
                        <div className="flex flex-row gap-2">
                          {properties.length > 0 ? (
                            properties.slice(0, 1).map((property) => (
                              <div
                              key={property.id}
                              className="flex flex-row gap-1 text-sm text-gray-700 truncate hover:text-gray-900"
                              title={property.name}
                              >
                              <MapPin size={18} className="text-gray-600" />
                                <span className="inline-block">{property.name}</span>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-gray-400 italic">Nenhuma propriedade associada</div>
                          )}
                          {properties.length > 1 && (
                            <div className=" bg-green-100 px-2 py-0.5 rounded-md text-xs text-green-600 font-medium">+{properties.length - 1} outras</div>
                          )}
                        </div>
                      </div>
                      {/* Chevron */}
                      <ChevronRight className="w-6 h-6 text-gray-500 group-hover:text-green-600 group-hover:translate-x-1 transition-transform shrink-0" />
                    </div>
                  </div>
                

                  {/* Access Badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Acesso:</span>
                    <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-md">
                      {role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* No Accounts State */}
        {filteredAndSortedAccounts.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter ? "Nenhuma conta encontrada para o filtro" : "Nenhuma conta encontrada"}
            </h3>
            <p className="text-gray-600 mb-4">
              {filter
                ? "Não encontramos contas correspondentes à sua busca. Tente outro termo."
                : "Você não tem acesso a nenhuma conta no momento."}
            </p>
            <Button onClick={handleBackToLogin} variant="primary" size="md">
              Voltar ao Login
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <Button
            onClick={handleBackToLogin}
            variant="ghost"
            size="md"
            icon={<ArrowLeft className="w-4 h-4" />}
            iconPosition="left"
          >
            Voltar ao login
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AccountSelectionPage;
